/**
 * Master Route Management Hook
 * 
 * Handles creation and navigation of master routes that combine multiple route segments
 */

import { useMemo } from 'react'
import type { RouteData } from '@cya-routes/shared'

interface UseMasterRouteProps {
  routes: RouteData[]
  selectedRouteId: string
  hasMasterRoute: boolean
  setSelectedRouteId: (id: string) => void
  zoomToRoute: (routeId: string) => void
}

interface UseMasterRouteReturn {
  createMasterRoute: () => RouteData | null
  getCurrentRoute: () => RouteData | null
  getNavigableRoutes: () => Array<{ id: string; name: string }>
  navigateToRoute: (direction: 'prev' | 'next') => void
  masterRoute: RouteData | null
}

export function useMasterRoute({
  routes,
  selectedRouteId,
  hasMasterRoute,
  setSelectedRouteId,
  zoomToRoute
}: UseMasterRouteProps): UseMasterRouteReturn {

  // Create master route from all segments
  const createMasterRoute = (): RouteData | null => {
    if (routes.length === 0) return null
    
    // Filter out excluded segments
    const includedRoutes = routes.filter(route => !route.excludeFromMaster)
    
    if (includedRoutes.length === 0) return null // All segments excluded
    if (includedRoutes.length === 1) return null // Only one segment included

    // Combine all route coordinates
    let allCoordinates: [number, number][] = []
    let allElevationProfile: Array<{ distance: number; elevation: number }> = []
    let totalDistance = 0
    let totalElevationUp = 0
    let totalElevationDown = 0
    let totalUnpaved = 0

    includedRoutes.forEach((route) => {
      if (route.coordinates) {
        // Add coordinates with distance offset
        const routeCoords = route.coordinates.map(coord => coord as [number, number])
        allCoordinates = [...allCoordinates, ...routeCoords]
      }

      if (route.elevation?.profile) {
        // Add elevation points with distance offset
        const offsetElevation = route.elevation.profile.map(point => ({
          distance: point.distance + totalDistance,
          elevation: point.elevation
        }))
        allElevationProfile = [...allElevationProfile, ...offsetElevation]
      }

      // Accumulate stats
      totalDistance += route.stats?.distance || 0
      totalElevationUp += route.elevation?.up || 0
      totalElevationDown += route.elevation?.down || 0
      totalUnpaved += (route.stats?.unpaved || 0) * (route.stats?.distance || 0) // Weight by distance
    })

    // Calculate average unpaved percentage
    const avgUnpaved = totalDistance > 0 ? totalUnpaved / totalDistance : 0

    // Combine surface breakdown statistics - MATCH HOMEPAGE LOGIC EXACTLY
    let totalUnpavedDistanceKm = 0 // Keep in km like HomePage
    const combinedHighwayStats: { [key: string]: { distance: number; percentage: number } } = {}

    includedRoutes.forEach((route) => {
      const segmentDistance = route.stats?.distance || 0 // in km
      
      if (route.stats?.surfaceBreakdown?.unpaved?.percentage !== undefined) {
        totalUnpavedDistanceKm += segmentDistance * (route.stats?.surfaceBreakdown?.unpaved?.percentage || 0) / 100
      } else if (route.stats?.unpaved !== undefined) {
        totalUnpavedDistanceKm += segmentDistance * (route.stats?.unpaved || 0) / 100
      }
      
      if (route.stats?.highwayBreakdown) {
        Object.entries(route.stats.highwayBreakdown).forEach(([highway, stats]) => {
          if (!combinedHighwayStats[highway]) {
            combinedHighwayStats[highway] = { distance: 0, percentage: 0 }
          }
          combinedHighwayStats[highway].distance += stats.distance
        })
      }
    })

    // Calculate percentages for highway breakdown
    Object.keys(combinedHighwayStats).forEach(highway => {
      combinedHighwayStats[highway].percentage = totalDistance > 0 
        ? (combinedHighwayStats[highway].distance / totalDistance) * 100 
        : 0
    })

    // Calculate final unpaved percentage - EXACTLY like HomePage
    const unpavedPercentage = totalDistance > 0 ? Math.round((totalUnpavedDistanceKm / totalDistance) * 100) : 0
    
    // Create surface breakdown
    const surfaceBreakdown = {
      paved: {
        distance: (totalDistance - totalUnpavedDistanceKm) * 1000, // Convert to meters for consistency
        percentage: 100 - unpavedPercentage
      },
      unpaved: {
        distance: totalUnpavedDistanceKm * 1000, // Convert to meters for consistency
        percentage: unpavedPercentage
      }
    }

    // Combine all surfaces
    let allSurfaces: string[] = []
    includedRoutes.forEach((route) => {
      if (route.surfaces && route.surfaces.length > 0) {
        allSurfaces = [...allSurfaces, ...route.surfaces]
      } else {
        // If no surface data, assume paved for the length of this route
        const routeLength = route.coordinates?.length || 0
        allSurfaces = [...allSurfaces, ...Array(routeLength).fill('paved')]
      }
    })

    // Combine unpaved segments with proper coordinate offset
    let allUnpavedSegments: [number, number][][] = []
    
    includedRoutes.forEach((route) => {
      if (route.unpavedSegments && route.unpavedSegments.length > 0) {
        // For now, just add the segments as-is since they're already in lat/lng coordinates
        // The segments should align with the combined coordinate array
        allUnpavedSegments = [...allUnpavedSegments, ...route.unpavedSegments]
      }
    })

    const masterRoute: RouteData = {
      id: 'master-route',
      name: 'Master Route',
      description: '', // Add empty description for master route
      coordinates: allCoordinates,
      elevation: {
        up: totalElevationUp,
        down: totalElevationDown,
        profile: allElevationProfile
      },
      stats: {
        distance: totalDistance,
        unpaved: Math.round(surfaceBreakdown.unpaved.percentage),
        surfaceBreakdown: surfaceBreakdown,
        highwayBreakdown: combinedHighwayStats
      },
      color: '#eb4d4b', // red - same as default route color
      surfaces: allSurfaces,
      unpavedSegments: allUnpavedSegments,
      location: includedRoutes[0]?.location || { country: '', state: '', city: '', region: '' },
      // Store individual segments for proper rendering without connections
      segments: includedRoutes.map(r => r.coordinates).filter(coords => coords && coords.length > 0)
    }

    return masterRoute
  }

  // Get current route (individual or master)
  const getCurrentRoute = (): RouteData | null => {
    if (selectedRouteId === 'master-route') {
      return masterRoute
    }
    return routes.find(r => r.id === selectedRouteId) || null
  }

  // Get all navigable route options
  const getNavigableRoutes = (): Array<{ id: string; name: string }> => {
    const navigableRoutes = routes.map(route => ({ id: route.id, name: route.name }))
    
    // Add master route if enabled and there are multiple routes
    if (hasMasterRoute && routes.length > 1) {
      navigableRoutes.push({ id: 'master-route', name: 'Master Route' })
    }
    
    return navigableRoutes
  }

  // Navigation between routes
  const navigateToRoute = (direction: 'prev' | 'next') => {
    const navigableRoutes = getNavigableRoutes()
    const currentIndex = navigableRoutes.findIndex(route => route.id === selectedRouteId)
    
    let newIndex: number
    if (direction === 'prev') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : navigableRoutes.length - 1
    } else {
      newIndex = currentIndex < navigableRoutes.length - 1 ? currentIndex + 1 : 0
    }
    
    const newRouteId = navigableRoutes[newIndex].id
    setSelectedRouteId(newRouteId)
    
    // Always zoom to the selected route
    zoomToRoute(newRouteId)
  }

  // Memoized master route
  const masterRoute = useMemo(() => {
    if (!hasMasterRoute || routes.length <= 1) return null
    return createMasterRoute()
  }, [routes, hasMasterRoute])

  return {
    createMasterRoute,
    getCurrentRoute,
    getNavigableRoutes,
    navigateToRoute,
    masterRoute
  }
}